import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as LoadingIcons from "react-loading-icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { createFactureFromDevis, deleteDevis, useGetDevis } from "../services/DevisService";
import { FaRegFileAlt, FaPaperPlane, FaCheckCircle, FaTimesCircle, FaHourglassEnd, FaExchangeAlt, FaList, FaEdit } from "react-icons/fa";
import { formatMoney } from "../utils/constante";
import { useSelector } from "react-redux";
import ActionDropdown from "./ActionDropdown";
import DateRangeInput from "./DateRangeInput";

const DevisList = () => {
    const location = useLocation();
    const token = useSelector((state) => state.auth.token);
    const [pdfLoading, setPdfLoading] = useState(true);
    const [filterStatut, setFilterStatut] = useState("Tous");
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [devisToDelete, setDevisToDelete] = useState(null);
    const [selectedPdf, setSelectedPdf] = useState(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [searchTermClient, setSearchTermClient] = useState("");
    const [searchTermNumeroDevis, setSearchTermNumeroDevis] = useState("");

    const navigate = useNavigate();
    const { devis, loading, pagination, page, setPage, fetchDevis, setDateRange, dateRange } = useGetDevis("Tous", {
        startDate: null,
        endDate: null
    });
    // Détecte si un filtre est actif
    const isFilter = Boolean(
        searchTermClient.trim() !== "" ||
        searchTermNumeroDevis.trim() !== "" ||
        (dateRange.startDate && dateRange.endDate)
    );

    const statutStyles = {
        Tous: { bg: "bg-gray-700", icon: <FaList className="inline mr-1" /> },
        brouillon: { bg: "bg-gray-600", icon: <FaRegFileAlt className="inline mr-1" /> },
        envoye: { bg: "bg-blue-700", icon: <FaPaperPlane className="inline mr-1" /> },
        accepte: { bg: "bg-green-700", icon: <FaCheckCircle className="inline mr-1" /> },
        refuse: { bg: "bg-red-700", icon: <FaTimesCircle className="inline mr-1" /> },
        expire: { bg: "bg-yellow-600", icon: <FaHourglassEnd className="inline mr-1" /> },
        transforme: { bg: "bg-purple-700", icon: <FaExchangeAlt className="inline mr-1" /> },
    };

    const statutStylesTelephone = {
        Tous: { bg: "bg-gray-700", icon: <FaList className="inline mr-1" size={12} /> },
        brouillon: { bg: "bg-gray-600", icon: <FaRegFileAlt className="inline mr-1" size={12} /> },
        envoye: { bg: "bg-blue-700", icon: <FaPaperPlane className="inline mr-1" size={12} /> },
        accepte: { bg: "bg-green-700", icon: <FaCheckCircle className="inline mr-1" size={12} /> },
        refuse: { bg: "bg-red-700", icon: <FaTimesCircle className="inline mr-1" size={12} /> },
        expire: { bg: "bg-yellow-600", icon: <FaHourglassEnd className="inline mr-1" size={12} /> },
        transforme: { bg: "bg-purple-700", icon: <FaExchangeAlt className="inline mr-1" size={12} /> },
    };
    const statutValues = {
        Tous: null,
        brouillon: "brouillon",
        envoye: "envoye",
        accepte: "accepte",
        refuse: "refuse",
        expire: "expire",
        transforme: "transforme",
    };

    const statutDisplay = {
        brouillon: "Brouillon",
        envoye: "Envoyé",
        accepte: "Accepté",
        refuse: "Refusé",
        expire: "Expiré",
        transforme: "Transformé",
    };

    const statutStylesTable = {
        Tous: { text: "text-gray-700", icon: <FaList className="inline mr-1" /> },
        brouillon: { text: "text-gray-600", icon: <FaRegFileAlt className="inline mr-1" /> },
        envoye: { text: "text-blue-700", icon: <FaPaperPlane className="inline mr-1" /> },
        accepte: { text: "text-green-700", icon: <FaCheckCircle className="inline mr-1" /> },
        refuse: { text: "text-red-700", icon: <FaTimesCircle className="inline mr-1" /> },
        expire: { text: "text-yellow-600", icon: <FaHourglassEnd className="inline mr-1" /> },
        transforme: { text: "text-purple-700", icon: <FaExchangeAlt className="inline mr-1" /> },
    };

    const statuts = ["Tous", "brouillon", "envoye", "accepte", "refuse", "expire", "transforme"];

    useEffect(() => {
        if (location.state?.message) {
            toast.success(location.state.message);
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        fetchDevis(page, filterStatut, dateRange, searchTermClient, searchTermNumeroDevis);
    }, [page, fetchDevis, filterStatut, dateRange, searchTermClient, searchTermNumeroDevis]);

    // Handler pour DateRangeInput
    const handleDateChange = ({ startDate, endDate }) => {
        setDateRange({ startDate, endDate });
    };

    const handleDeleteClick = (devis) => {
        setDevisToDelete(devis);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!devisToDelete) return;
        try {
            const result = await deleteDevis(devisToDelete.id);
            toast.success(result.message);
            fetchDevis(page, filterStatut);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDevisToDelete(null);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.last_page) setPage(newPage);
    };

    const openPdf = (id) => {
        const pdfUrl = `http://localhost:8000/api/v1/devis/${id}/pdf?token=${token}`;
        setSelectedPdf(pdfUrl);
        setPdfLoading(true);
    };

    const handleTransfert = async (devis) => {
        try {
            const result = await createFactureFromDevis(devis.id);
            toast.success(result.message || "Facture créée avec succès");
        } catch (error) {
            toast.error(error.message || "Erreur lors du transfert du devis");
        }
    };

    return (
        <section className="pb-4 flex flex-col gap-3 justify-center lg:px-4 px-2 w-full">
            <ToastContainer position="top-right" autoClose={3000} />
            <DeleteConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                clientName={devisToDelete?.numero}
            />

            {selectedPdf && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-transparent w-11/12 h-full rounded-lg shadow-lg p-2 relative">
                        <button
                            onClick={() => setSelectedPdf(null)}
                            className="absolute top-2 right-2 text-white bg-red-600 px-1 py-1/2 rounded"
                        >
                            X
                        </button>

                        {pdfLoading && (
                            <div className="flex justify-center items-center h-full">
                                <LoadingIcons.SpinningCircles stroke="#fff" fill="#fff" strokeWidth={3} height={40} width={40} />
                                <span className="ml-3 text-white font-medium">Chargement du PDF...</span>
                            </div>
                        )}

                        <iframe
                            src={selectedPdf}
                            title="Devis PDF"
                            className={`w-full h-full rounded-lg ${pdfLoading ? "hidden" : ""}`}
                            onLoad={() => setPdfLoading(false)}
                        />
                    </div>
                </div>
            )}

            {/* Filtre + titre */}
            <div className="flex xl:flex-row flex-col w-full lg:items-center items-start justify-between gap-2 lg:py-2">
                {/* Titre */}
                <div className="flex flex-row justify-between items-center  w-full">
                    <h1 className="font-bold font-Poppins lg:text-3xl text-2xl text-color11 whitespace-nowrap">
                        Liste des Devis
                    </h1>
                    {isFilter && (
                        <p
                            onClick={() => {
                                setSearchTermClient("");
                                setSearchTermNumeroDevis("");
                                setDateRange({ startDate: null, endDate: null }); // 🔹 ça déclenchera le useEffect
                                handlePageChange(1); // 🔹 remettre la page à 1
                            }}
                            className="lg:hidden block cursor-pointer w-auto rounded-full  py-2 text-color16"
                        >
                            Réinitialiser
                        </p>
                    )}
                </div>

                {/* Desktop : filtres + bouton Ajouter */}
                <div className="hidden xl:flex items-center gap-2">
                    <p className="text-sm font-bold font-Poppins">Filtrer par :</p>
                    {statuts.map((s) => {
                        const { bg, icon } = statutStyles[s];
                        const isActive = filterStatut === statutValues[s];
                        return (
                            <button
                                key={s}
                                onClick={() => setFilterStatut(statutValues[s])}
                                className={`flex items-center px-3 py-1 rounded-md text-xs font-semibold transition-colors duration-200 
            ${isActive ? `${bg} text-white shadow-md` : `${bg} opacity-100 hover:opacity-80 text-white`}`}
                            >
                                {icon} {s === "Tous" ? "Tous" : statutDisplay[s]}
                            </button>
                        );
                    })}
                    <button
                        className="bg-color3 hover:bg-green-600 text-white px-3 py-2 rounded-lg flex items-center gap-1 text-xs"
                        onClick={() => navigate("/EspaceClient/devis/ajouter")}
                    >
                        <FaEdit /> Ajouter un devis
                    </button>
                </div>

                {/* Mobile : bouton Ajouter */}
                <div className="xl:hidden flex justify-between w-full  items-center">
                    <p className="text-sm font-bold font-Poppins">Filtrer par :</p>
                    <button
                        className="bg-color3 hover:bg-green-600 text-white px-3 py-2 rounded-lg flex items-center gap-1 text-xs"
                        onClick={() => navigate("/EspaceClient/devis/ajouter")}
                    >
                        <FaEdit size={15} /> Ajouter un devis
                    </button>
                </div>
                {/* Mobile : filtres scrollables */}
                <div className="xl:hidden items-center justify-start flex overflow-x-auto max-w-full gap-2 pt-3 pb-1 pr-2">
                    {statuts.map((s) => {
                        const { bg, icon } = statutStylesTelephone[s];
                        const isActive = filterStatut === statutValues[s];
                        return (
                            <button
                                key={s}
                                onClick={() => setFilterStatut(statutValues[s])}
                                className={`flex items-center justify-center px-3 py-1 rounded-md text-xs font-semibold transition-colors duration-200
                             ${isActive ? `${bg} text-white shadow-md` : `${bg} opacity-100 hover:opacity-80 text-white`}`}
                            >
                                {icon} {s === "Tous" ? "Tous" : statutDisplay[s]}
                            </button>
                        );
                    })}
                </div>

            </div>
            <div className="flex flex-row w-full items-center gap-2 min-w-0">
                <input
                    type="text"
                    placeholder="Filtrer par numero de devis ..."
                    className="flex-1 px-4 py-2 border border-color1 rounded-md focus:outline-none focus:ring-2 focus:ring-color1 focus:border-color1 min-w-0"
                    value={searchTermNumeroDevis}
                    onChange={(e) => setSearchTermNumeroDevis(e.target.value)}
                />
                <input
                    type="text"
                    placeholder="Filtrer par nom du client ..."
                    className="flex-1 px-4 py-2 border border-color1 rounded-md focus:outline-none focus:ring-2 focus:ring-color1 focus:border-color1 min-w-0"
                    value={searchTermClient}
                    onChange={(e) => setSearchTermClient(e.target.value)}
                />
            </div>

            <div className="flex lg:flex-row flex-col w-full ">
                <DateRangeInput
                    initialStartDate={dateRange.startDate}
                    initialEndDate={dateRange.endDate}
                    onChange={handleDateChange}
                    title2="Filtrer par Date Émission : "
                />
                {isFilter && (
                    <p
                        onClick={() => {
                            setSearchTermClient("");
                            setSearchTermNumeroDevis("");
                            setDateRange({ startDate: null, endDate: null }); // 🔹 ça déclenchera le useEffect
                            handlePageChange(1); // 🔹 remettre la page à 1
                        }}
                        className="lg:flex hidden cursor-pointer w-auto rounded-full px-4 py-2 text-color16"
                    >
                        Réinitialiser
                    </p>
                )}

            </div>
            {/* Tableau */}
            <div className="relative overflow-x-auto shadow-md rounded-xl border border-gray-200 bg-white">
                <table className="w-full text-sm text-left border-collapse relative z-0">
                    <thead className="bg-color1 text-color2 uppercase text-xs font-semibold">
                        <tr>
                            <th className="px-3 py-2 w-[40px] text-center">#</th>
                            <th className="px-3 py-2 w-[120px] text-center">Numéro</th>
                            <th className="px-3 py-2 w-[150px] text-center">Client</th>
                            <th className="px-3 py-2 w-[120px] text-center">Date émission</th>
                            <th className="px-3 py-2 w-[120px] text-center">Date expiration</th>
                            <th className="px-3 py-2 w-[120px] text-center">Statut</th>
                            <th className="px-3 py-2 w-[120px] text-center">Total HT</th> {/* ✅ changé */}
                            <th className="px-3 py-2 w-[100px] text-center">Remise</th>
                            <th className="px-3 py-2 w-[100px] text-center">TVA</th>
                            <th className="px-3 py-2 w-[120px] text-center">Total TTC</th> {/* ✅ plus clair */}
                            <th className="px-3 py-2 w-[160px] text-center">Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="11" className="lg:h-[450px] h-[100px] text-center">
                                    <div className="flex lg:flex-row  flex-col  justify-center items-center">
                                        <LoadingIcons.SpinningCircles
                                            stroke="#2218eeff"
                                            fill="#2218eeff"
                                            strokeWidth={3}
                                            height={40}
                                            width={40}
                                        />
                                        <span className="ml-3 text-gray-600 font-medium">
                                            Chargement des devis...
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        ) : devis && devis.length > 0 ? (
                            devis.map((d, index) => (
                                <tr
                                    key={d.id}
                                    className={`border-b hover:bg-gray-100 transition ${index % 2 === 0 ? "bg-gray-50" : "bg-white"
                                        }`}
                                >
                                    <td className="px-3 py-3 text-center font-medium text-gray-700">
                                        {index + 1 + (pagination.current_page - 1) * pagination.per_page}
                                    </td>
                                    <td className="px-3 py-3 text-center">{d.numero}</td>
                                    <td className="px-3 py-3 text-center">
                                        {d.client?.nom_affichage || "-"}
                                    </td>
                                    <td className="px-3 py-3 text-center">{d.date_emission || "-"}</td>
                                    <td className="px-3 py-3 text-center">{d.date_expiration || "-"}</td>
                                    <td className="px-3 py-3 text-center">
                                        <span
                                            className={`flex items-center justify-center font-semibold ${statutStylesTable[d.statut]?.text
                                                }`}
                                        >
                                            {statutStylesTable[d.statut]?.icon}{" "}
                                            {statutDisplay[d.statut] || d.statut}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3 text-center">
                                        {formatMoney(d.sous_total_centimes, d.devise)}
                                    </td>
                                    <td className="px-3 py-3 text-center">
                                        {formatMoney(d.remise_centimes, d.devise)}
                                    </td>
                                    <td className="px-3 py-3 text-center">
                                        {formatMoney(d.tva_centimes, d.devise)}
                                    </td>
                                    <td className="px-3 py-3 text-center font-bold">
                                        {formatMoney(d.total_centimes, d.devise)}
                                    </td>
                                    <td className="px-3 py-3 text-center relative z-20">
                                        <ActionDropdown
                                            d={d}
                                            navigate={navigate}
                                            handleDeleteClick={handleDeleteClick}
                                            openPdf={openPdf}
                                            handleTransfert={handleTransfert}
                                            isOpen={openDropdownId === d.id}
                                            onToggle={() => setOpenDropdownId(openDropdownId === d.id ? null : d.id)}
                                            onClose={() => setOpenDropdownId(null)}
                                        />

                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="11" className="py-4 text-center text-gray-500">
                                    Aucun devis trouvé.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>


            {/* Pagination */}
            {pagination.last_page > 1 && (
                <div className="flex w-full justify-center items-center gap-3 mt-4">
                    <button
                        disabled={pagination.current_page === 1}
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        className={`px-4 py-1 rounded-lg text-white text-sm font-semibold ${pagination.current_page === 1 ? "bg-gray-400 cursor-not-allowed" : "bg-color1 hover:bg-color6 cursor-pointer"
                            }`}
                    >
                        Précédent
                    </button>

                    <span className="text-color11 font-medium text-sm">
                        {pagination.current_page} / {pagination.last_page}
                    </span>

                    <button
                        disabled={pagination.current_page === pagination.last_page}
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        className={`px-4 py-1 rounded-lg text-white text-sm font-semibold ${pagination.current_page === pagination.last_page ? "bg-gray-400 cursor-not-allowed" : "bg-color1 hover:bg-color6 cursor-pointer"
                            }`}
                    >
                        Suivant
                    </button>
                </div>
            )}

        </section>
    );
};

export default DevisList;
